import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogActions, DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { getPoliciesAPI } from 'apis/settings/settingsAPI';
import { postPrivacyAPI, postTermsAPI, postSecurityAPI } from 'apis/policy/policyAPI';
import './MenuCommon.css';
import Tiptap from '../Tiptap/Tiptap';
import Loading from 'components/Loading';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSnackbarStore } from 'store/useSnackbarStore';
import SnackbarAlert from '../SnackbarAlert';
import { DatePicker } from '@mui/x-date-pickers';

// 첫 번째 텍스트 노드의 연속된 공백을 &nbsp;로 변환하는 함수 (TypeScript-safe)
export const preserveLeadingSpaces = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const replaceLeadingSpaces = (node: ChildNode) => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      node.textContent = node.textContent.replace(/^\s+/, (spaces) => spaces.replace(/ /g, '\u00A0'));
    } else {
      Array.from(node.childNodes).forEach(replaceLeadingSpaces);
    }
  };

  Array.from(doc.body.childNodes).forEach(replaceLeadingSpaces);

  return doc.body.innerHTML;
};

export default function PolicyManagement() {
  const termsEditorRef = useRef<any>(null);
  const termsEnglishEditorRef = useRef<any>(null);
  const termsJapaneseEditorRef = useRef<any>(null);
  const privacyEditorRef = useRef<any>(null);
  const privacyEnglishEditorRef = useRef<any>(null);
  const privacyJapaneseEditorRef = useRef<any>(null);
  const securityEditorRef = useRef<any>(null);
  const securityEnglishEditorRef = useRef<any>(null);
  const securityJapaneseEditorRef = useRef<any>(null);

  const [termsContent, setTermsContent] = useState<string>('');
  const [termsEnglishContent, setTermsEnglishContent] = useState<string>('');
  const [termsJapaneseContent, setTermsJapaneseContent] = useState<string>('');
  const [privacyContent, setPrivacyContent] = useState<string>('');
  const [privacyEnglishContent, setPrivacyEnglishContent] = useState<string>('');
  const [privacyJapaneseContent, setPrivacyJapaneseContent] = useState<string>('');
  const [securityContent, setSecurityContent] = useState<string>('');
  const [securityEnglishContent, setSecurityEnglishContent] = useState<string>('');
  const [securityJapaneseContent, setSecurityJapaneseContent] = useState<string>('');

  const [termsMsg, setTermsMsg] = useState('');
  const [privacyMsg, setPrivacyMsg] = useState('');
  const [securityMsg, setSecurityMsg] = useState('');

  const [newVersionPopupOpen, setNewVersionPopupOpen] = useState<boolean>(false);
  const [privacyVersion, setPrivacyVersion] = useState('-');
  const [pvKorean, setPvKorean] = useState('-');
  const [pvEnglish, setPvEnglish] = useState('-');
  const [pvJapanese, setPvJapanese] = useState('-');
  const [privacyCheckedNewVersion, setPrivacyCheckedNewVersion] = useState(false);

  const [termsSelectedLanguage, setTermsSelectedLanguage] = useState('ko');
  const [privacySelectedLanguage, setPrivacySelectedLanguage] = useState('ko');
  const [securitySelectedLanguage, setSecuritySelectedLanguage] = useState('ko');

  const [privacyNewDate, setPrivacyNewDate] = useState<Dayjs | null>(dayjs());

  const [isLoading, setIsLoading] = useState(true);
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 스낵바
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const systemAPIResponse = await getPoliciesAPI();
      const {
        terms,
        termsEnglish,
        termsJapanese,
        privacyVersion,
        privacyEnglishVersion,
        privacyJapaneseVersion,
        privacy,
        privacyEnglish,
        privacyJapanese,
        security,
        securityEnglish,
        securityJapanese
      } = systemAPIResponse.data;

      // 이용약관
      setTermsContent(preserveLeadingSpaces(terms || ''));
      setTermsEnglishContent(preserveLeadingSpaces(termsEnglish || ''));
      setTermsJapaneseContent(preserveLeadingSpaces(termsJapanese || ''));
      // 개인정보처리
      setPrivacyVersion(privacyVersion);
      setPvKorean(privacyVersion);
      setPvEnglish(privacyEnglishVersion);
      setPvJapanese(privacyJapaneseVersion);
      setPrivacyContent(preserveLeadingSpaces(privacy || ''));
      setPrivacyEnglishContent(preserveLeadingSpaces(privacyEnglish || ''));
      setPrivacyJapaneseContent(preserveLeadingSpaces(privacyJapanese || ''));
      // 보안서약
      setSecurityContent(preserveLeadingSpaces(security || ''));
      setSecurityEnglishContent(preserveLeadingSpaces(securityEnglish || ''));
      setSecurityJapaneseContent(preserveLeadingSpaces(securityJapanese || ''));

      setIsLoading(false);
    } catch (error) {
      console.error('데이터 가져오기 실패 :', error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const isTermsSelected = (language: string) => termsSelectedLanguage === language;

  const handlePrivacyLanguageChange = (language: string) => {
    setPrivacySelectedLanguage(language);

    if (language === 'ko') {
      setPrivacyVersion(pvKorean);
    } else if (language === 'en') {
      setPrivacyVersion(pvEnglish);
    } else if (language === 'ja') {
      setPrivacyVersion(pvJapanese);
    }
    setPrivacyCheckedNewVersion(false);
  };

  const isPrivacySelected = (language: string) => privacySelectedLanguage === language;

  const isSecuritySelected = (language: string) => securitySelectedLanguage === language;
  const handleSaveTerms = async () => {
    const languageContentMap: { [key: string]: any } = {
      ko: termsEditorRef?.current?.getHTML(),
      en: termsEnglishEditorRef?.current?.getHTML(),
      ja: termsJapaneseEditorRef?.current?.getHTML()
    };

    const termsContent = languageContentMap[termsSelectedLanguage] || '';
    try {
      const response = await postTermsAPI(String(termsContent), termsSelectedLanguage);
      if (response.status === 200) {
        if (termsSelectedLanguage === 'ko') {
          setTermsContent(preserveLeadingSpaces(termsContent || ''));
        } else if (termsSelectedLanguage === 'en') {
          setTermsEnglishContent(preserveLeadingSpaces(termsContent || ''));
        } else if (termsSelectedLanguage === 'ja') {
          setTermsJapaneseContent(preserveLeadingSpaces(termsContent || ''));
        }

        setTermsMsg('이용약관을 저장하였습니다.');
        setTimeout(() => {
          setTermsMsg('');
        }, 3000);
      }
    } catch (error) {
      console.error('이용약관 저장 실패:', error);
    }
  };

  const handleCheckNewVersion = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (dayjs().format('YYYY-MM-DD') === privacyVersion) {
      setPrivacyCheckedNewVersion(false);
    } else {
      setPrivacyCheckedNewVersion(checked);
    }
  };

  const handleNewVersionPopupOpen = () => {
    setNewVersionPopupOpen(true);
  };
  const handleNewVersionPopupClose = () => {
    setNewVersionPopupOpen(false); // 팝업 닫기
  };

  const handleSavePrivacy = async () => {
    if (privacyCheckedNewVersion) {
      handleNewVersionPopupOpen();
    } else {
      await handleSavePrivacyConfirm();
    }
  };
  const handleSavePrivacyConfirm = async () => {
    const languageContentMap: { [key: string]: any } = {
      ko: privacyEditorRef?.current?.getHTML(),
      en: privacyEnglishEditorRef?.current?.getHTML(),
      ja: privacyJapaneseEditorRef?.current?.getHTML()
    };

    const privacyContent = languageContentMap[privacySelectedLanguage] || '';
    try {
      const response = await postPrivacyAPI(privacyCheckedNewVersion, privacyNewDate?.format('YYYY-MM-DD') || null, String(privacyContent), privacySelectedLanguage);
      if (response.status === 200) {
        if (privacySelectedLanguage === 'ko') {
          setPrivacyContent(preserveLeadingSpaces(privacyContent || ''));
        } else if (privacySelectedLanguage === 'en') {
          setPrivacyEnglishContent(preserveLeadingSpaces(privacyContent || ''));
        } else if (privacySelectedLanguage === 'ja') {
          setPrivacyJapaneseContent(preserveLeadingSpaces(privacyContent || ''));
        }

        setPrivacyMsg('개인정보처리방침을 저장하였습니다.');
        setTimeout(() => {
          setPrivacyMsg('');
        }, 3000);

        setPrivacyCheckedNewVersion(false);
        setNewVersionPopupOpen(false);

        await fetchSettings();
      }
    } catch (error) {
      // console.error('개인정보처리방침 저장 실패:', error);
      const errorMessage = (error as { data: { message: '' } })?.data?.message || '개인정보처리방침 저장 실패';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleSaveSecurity = async () => {
    const languageContentMap: { [key: string]: any } = {
      ko: securityEditorRef?.current?.getHTML(),
      en: securityEnglishEditorRef?.current?.getHTML(),
      ja: securityJapaneseEditorRef?.current?.getHTML()
    };

    const securityContent = languageContentMap[securitySelectedLanguage] || '';
    try {
      const response = await postSecurityAPI(String(securityContent), securitySelectedLanguage);
      if (response.status === 200) {
        if (securitySelectedLanguage === 'ko') {
          setSecurityContent(preserveLeadingSpaces(securityContent || ''));
        } else if (securitySelectedLanguage === 'en') {
          setSecurityEnglishContent(preserveLeadingSpaces(securityContent || ''));
        } else if (securitySelectedLanguage === 'ja') {
          setSecurityJapaneseContent(preserveLeadingSpaces(securityContent || ''));
        }

        setSecurityMsg('보안서약을 저장하였습니다.');
        setTimeout(() => {
          setSecurityMsg('');
        }, 3000);
      }
    } catch (error) {
      console.error('보안서약 저장 실패:', error);
    }
  };

  // 언어 타입을 명시적으로 정의
  type Language = 'ko' | 'en' | 'ja';

  const contentsMap = {
    terms: {
      ko: { content: termsContent, ref: termsEditorRef },
      en: { content: termsEnglishContent, ref: termsEnglishEditorRef },
      ja: { content: termsJapaneseContent, ref: termsJapaneseEditorRef }
    },
    privacy: {
      ko: { content: privacyContent, ref: privacyEditorRef },
      en: { content: privacyEnglishContent, ref: privacyEnglishEditorRef },
      ja: { content: privacyJapaneseContent, ref: privacyJapaneseEditorRef }
    },
    security: {
      ko: { content: securityContent, ref: securityEditorRef },
      en: { content: securityEnglishContent, ref: securityEnglishEditorRef },
      ja: { content: securityJapaneseContent, ref: securityJapaneseEditorRef }
    }
  };
  // 언어를 `Language` 타입으로 명확히 지정
  const renderContent = (type: keyof typeof contentsMap, selectedLanguage: Language) => {
    const { content, ref } = contentsMap[type][selectedLanguage] || { content: '', ref: null };
    return isLoading ? <Loading /> : <Tiptap ref={ref} content={content} maxHeight="600px" />;
  };

  interface LanguageButtonGroupProps {
    selectedLanguage: string;
    onLanguageChange: (language: Language) => void;
  }

  const LanguageButtonGroup = ({ selectedLanguage, onLanguageChange }: LanguageButtonGroupProps) => {
    const theme = useTheme();

    return (
      <ButtonGroup size="small" color="inherit" sx={{ height: '28px' }}>
        {['ko', 'en', 'ja'].map((lang) => (
          <Button
            key={lang}
            sx={{
              borderRadius: '4px',
              borderColor: 'black',
              bgcolor: selectedLanguage === lang ? theme.palette.selectButton.selected : theme.palette.selectButton.unselected,
              color: selectedLanguage === lang ? theme.palette.selectButton.selectedText : theme.palette.selectButton.unselectedText
            }}
            onClick={() => onLanguageChange(lang as Language)}
          >
            {lang === 'ko' ? t('policy_management.ko') : lang === 'en' ? t('policy_management.en') : t('policy_management.ja')}
          </Button>
        ))}
      </ButtonGroup>
    );
  };
  interface SaveButtonProps {
    onSave: () => void;
    label: string;
  }

  const SaveButton = ({ onSave, label }: SaveButtonProps) => (
    <Box>
      <Button
        onClick={onSave}
        variant="contained"
        sx={{
          // minWidth: '100px', // 최소 너비 설정
          // maxWidth: '230px', // 최대 너비 설정
          paddingX: '16px', // 버튼 내 여백 설정
          fontWeight: 'bold', // 텍스트 스타일링
          borderRadius: '5px', // 버튼 테두리 둥글게 설정
          whiteSpace: 'nowrap' // 텍스트 줄바꿈 방지
          // textAlign: 'center' // 텍스트 가운데 정렬
        }}
        fullWidth={false}
      >
        {label}
      </Button>
    </Box>
  );

  return (
    <>
      <SnackbarAlert />
      <Box padding={2} display="flex" flexDirection="column" gap={2} sx={{ width: { xs: '100vw', sm: '100%' } }}>
        <Box
          // padding={2}
          // paddingBottom={2}
          borderRadius={1}
          // bgcolor={theme.palette.contentBlock.background}
          display="flex"
          flexDirection="column"
          gap={1}
        >
          <Typography variant="h5" fontWeight="bold">
            {t('policy_management.title')}
          </Typography>

          <Divider flexItem />
        </Box>

        <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="row" gap={1}>
            <Typography variant="h5" fontWeight="bold">
              {t('policy_management.term')}
            </Typography>
            <LanguageButtonGroup selectedLanguage={termsSelectedLanguage} onLanguageChange={setTermsSelectedLanguage} />
          </Box>

          {/* 데이터가 존재하지 않을 때 로딩 메시지 또는 스피너 표시 */}
          {/* 위의 코드를 아래로 리팩토링 */}
          <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.main}>
            {renderContent('terms', termsSelectedLanguage as Language)}
          </Box>

          {termsMsg && <Alert severity="success">{termsMsg}</Alert>}
          <SaveButton onSave={handleSaveTerms} label={t('policy_management.save_term')} />
        </Box>

        <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between">
            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <Typography variant="h5" fontWeight="bold">
                {t('policy_management.privacy')}
              </Typography>
              <LanguageButtonGroup selectedLanguage={privacySelectedLanguage} onLanguageChange={setPrivacySelectedLanguage} />
              {/* 전에 코드는 handlePrivacyLanguageChange 이걸로 변경했음 */}
            </Box>

            <Box display="flex" flexDirection="row" gap={1} alignItems="center">
              <Box>
                {t('policy_management.cur_version')} {privacyVersion}
              </Box>
              <Divider orientation="vertical" flexItem sx={{ marginY: 1 }} />
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={privacyCheckedNewVersion} onChange={handleCheckNewVersion} sx={{ padding: 0.5, marginLeft: 0.5 }} />}
                  label={t('policy_management.new_version')}
                />
              </Box>
            </Box>
          </Box>

          <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.main}>
            {renderContent('privacy', privacySelectedLanguage as Language)}
          </Box>

          {privacyMsg && <Alert severity="success">{privacyMsg}</Alert>}
          <SaveButton onSave={handleSavePrivacy} label={t('policy_management.save_privacy')} />
        </Box>

        <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" flexDirection="row" gap={1}>
            <Typography variant="h5" fontWeight="bold">
              {t('policy_management.security')}
            </Typography>
            <LanguageButtonGroup selectedLanguage={securitySelectedLanguage} onLanguageChange={setSecuritySelectedLanguage} />
          </Box>

          <Box boxShadow={1} borderRadius={1} bgcolor={theme.palette.tiptap.main}>
            {renderContent('security', securitySelectedLanguage as Language)}
          </Box>

          {securityMsg && <Alert severity="success">{securityMsg}</Alert>}
          <SaveButton onSave={handleSaveSecurity} label={t('policy_management.save_security')} />
        </Box>
      </Box>

      <Dialog open={newVersionPopupOpen} onClose={handleNewVersionPopupClose}>
        <DialogTitle>
          {t('policy_management.privacy_dialog_content1')}
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          {t('policy_management.privacy_dialog_content2')}
          <DatePicker
            format="YYYY-MM-DD"
            value={privacyNewDate}
            onChange={(newValue) => setPrivacyNewDate(newValue)}
            minDate={dayjs(privacyVersion) ? dayjs(privacyVersion).add(1, 'day') : dayjs().add(-1, 'year')}
            slotProps={{
              calendarHeader: {
                format: 'YYYY-MM'
              },
              textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
            }}
            sx={{
              width: '140px',
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem' // 아이콘 크기 조정
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box>
            <Button onClick={handleNewVersionPopupClose} color="inherit">
              {t('policy_management.cancel')}
            </Button>
            <Button onClick={handleSavePrivacyConfirm} color="primary">
              {t('policy_management.ok')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
